import { useRef, useState } from 'react'
import { NavLink, useSearchParams } from 'react-router'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import Add from '@mui/icons-material/Add'
import Cached from '@mui/icons-material/Cached'
import Archive from '@mui/icons-material/Archive'

import { useBillingApi } from '../../api/billing/billing-context'
import { Customer, ListCustomerSortableField, Role } from 'common-billing-server/types'
import { makeEditCustomerPath, Path } from '../../routes/path'
import { Column, DataFetchingPaginatedTable, RefreshableDataFetchingPaginatedTable } from '../common/Table'
import { Query } from 'common/query'
import { SortOrder } from 'common/api/v1/types'
import { useConfirmationDialog } from '../common/ConfirmationDialog'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useStatefulNavigate } from '../common/hooks/hook-stateful-navigate'
import { SelectionToolbar } from '../common/ListPageToolbar'
import Wrapper from '../common/Wrapper'
import { RoleBasedComponent } from '../common/RoleBasedLink'
import { formatDate } from '../../format-date'
import { RotatingButton } from '../common/RotatingButton'
import { PaginatedDataFetchingFilter } from '../common/hooks/hook-paginated-data-fetching'

const columns: Column<Customer, ListCustomerSortableField>[] = [
    { title: 'Name', valueForColumn: (c) => c.name, sortField: ListCustomerSortableField.name },
    { title: 'Customer number', valueForColumn: (c) => c.customerNumber },
    { title: 'Billing type', valueForColumn: (c) => c.billingType },
    { title: 'Billing start (UTC)', valueForColumn: (c) => formatDate(c.billingStartsAt) },
]

const columnsWithStatus: Column<Customer, ListCustomerSortableField>[] = [
    ...columns,
    { title: 'Status', valueForColumn: (c) => (c.deleted ? 'Archived' : 'Active') },
]

export const ListCustomersPage = () => {
    const billingApi = useBillingApi()
    const [searchParams] = useSearchParams()
    const showArchived = searchParams.get('showDeleted') === 'true'
    const { navigate } = useStatefulNavigate()
    const navigateToCustomer = (customer: Customer) => navigate(makeEditCustomerPath(customer.id))
    const tableRef = useRef<RefreshableDataFetchingPaginatedTable>(null)
    const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([])
    const showConfirmArchiveDialog = useConfirmationDialog()

    const { addFailedOperation, errorBanner } = useFailedOperationBanner()

    async function fetchCustomers({
        filter,
        order,
        ...query
    }: Query<PaginatedDataFetchingFilter<{ showDeleted: boolean }>, SortOrder<ListCustomerSortableField>>) {
        try {
            return await billingApi.listCustomers({
                ...query,
                filter: { searchName: filter?.searchString, excludeDeleted: !filter?.boolFilters?.showDeleted },
                order,
            })
        } catch (error) {
            addFailedOperation({
                id: 'list-customers',
                message: 'Failed fetching customers',
                retryFn: tableRef.current?.refreshData,
            })
            throw error
        }
    }

    const onArchiveSelectedRowsClicked = () => {
        async function archiveSelectedCustomers(customers: Customer[]) {
            const ids = customers.map((c) => c.id)
            try {
                const { notDeleted } = await billingApi.deleteCustomers({ ids })
                if (notDeleted.length > 0) {
                    const customersNotArchived = customers.filter(({ id }) => notDeleted.includes(id))
                    addFailedOperation({
                        id: notDeleted.join(','),
                        message: `Failed archiving ${notDeleted.length} customer(s)`,
                        retryFn: () => void archiveSelectedCustomers(customersNotArchived),
                    })
                }
            } catch (error) {
                addFailedOperation({
                    id: ids.join(','),
                    message: `Failed archiving customers`,
                    error,
                    retryFn: () => void archiveSelectedCustomers(customers),
                })
            }

            tableRef.current?.refreshData()
        }

        showConfirmArchiveDialog(
            () => {
                void archiveSelectedCustomers(selectedCustomers)
            },
            `Archive ${selectedCustomers.length} customer(s)? Existing licenses, installations, user access, and billing remain active.`,
            { ok: { text: 'Archive', startIcon: <Archive /> } }
        )
    }

    return (
        <Wrapper
            actions={
                <>
                    <RotatingButton
                        key="refresh"
                        tooltip="Refresh"
                        onClick={() => tableRef.current?.refreshData()}
                        Component={<Cached />}
                    />
                    <RoleBasedComponent key="create" minimumAllowedRole={Role.admin}>
                        <NavLink to={Path.createCustomer}>
                            <Tooltip title="Create new customer">
                                <IconButton>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        </NavLink>
                    </RoleBasedComponent>
                </>
            }
        >
            <SelectionToolbar
                numberOfSelectedItems={selectedCustomers.length}
                selectedActions={[
                    <RoleBasedComponent key="archive" minimumAllowedRole={Role.super}>
                        <Tooltip title="Archive">
                            <IconButton aria-label="Archive selected customers" onClick={onArchiveSelectedRowsClicked}>
                                <Archive />
                            </IconButton>
                        </Tooltip>
                    </RoleBasedComponent>,
                ]}
            />
            <Paper>
                <DataFetchingPaginatedTable
                    myRef={tableRef}
                    api={fetchCustomers}
                    columns={showArchived ? columnsWithStatus : columns}
                    emptyRowsMessage={'No customers found'}
                    onRowClicked={navigateToCustomer}
                    onRowsSelected={setSelectedCustomers}
                    boolFilters={[{ filterName: 'showDeleted', label: 'Show archived customers', initialValue: false }]}
                    searchParameters={{
                        searchBarPlaceholder: 'Search customer by name, customer number or PO number...',
                    }}
                />
            </Paper>

            {errorBanner}
        </Wrapper>
    )
}
